import React, { useContext, useState, useEffect, useCallback } from 'react'
import { Grid, Cell, Accordion, AccordionItem, AccordionTitle, AccordionContent } from 'react-foundation'
import format from 'date-fns/format'
import map from 'lodash/map'
import fill from 'lodash/fill'
import UserContext, { Roles } from '../../UserContext'
import { ApiFetch } from './Api'
import { GroupBy } from './Utility'
import type { Audit } from '../../models'
import ViewNotificatonDetails from './ViewNotificationDetails'

type ViewAuditsProps = { 
    objectType: string
    objectID: number
    auditTypeFilter?: string[]
    title?: string
    activeTab?: string 
}

const ViewAudits: React.FC<ViewAuditsProps> = ({ objectType, objectID, auditTypeFilter, title, activeTab }) => {
    const userContext = useContext(UserContext)            
    const [error, setError] = useState<string>('')
    const [audits, setAudits] = useState<Audit[]>()
    const [showDescription, setShowDescription]= useState<boolean[]>([])
    const [loading, setLoading] = useState<boolean>(false)
    const isActive = activeTab === 'Changes'

    const toggleDescription = useCallback((id: number ) => {
        const list = showDescription
        list[id] = !list[id]
        setShowDescription([...list])
    }, [showDescription])
    
    useEffect(()=>{
        if(!isActive || userContext.hasAnyRole(Roles.Provider)) return
        setError('')
        setLoading(true)
        ApiFetch<Audit[]>('Audit', { targetObjectType: objectType, targetObjectID: objectID })
        .then(  
            (result) => {                
                if (auditTypeFilter)
                    setAudits(result.filter(a => auditTypeFilter.indexOf(a.auditType) >= 0))
                else
                    setAudits(result)
                const showList = map(result, 'serviceRequestAuditID')
                const auditList = fill(showList, false)
                setShowDescription(auditList)
                setLoading(false)
            },        
            () => {    
                setError('Failed to retrieve audit changes.') 
                setAudits([])      
                setLoading(false)                       
            }
        )        
    },[objectType, objectID, userContext, auditTypeFilter, isActive])

    const renderDescription = (audit: Audit) => {
        if(audit.auditType === 'Notification') {
            //Extract out the notification ID, and make it clickable
            const match = /ID#(\d+)/.exec(audit.changeDescription)
            if(match && match.length >= 2) {
                const id = parseInt(match[1])
                const idStr = 'ID#' + match[1]
                const index = audit.changeDescription.indexOf(idStr)
                const newDesc = audit.changeDescription.replace(idStr, '')

                return <ViewNotificatonDetails notificationID={id} preContent={newDesc.substring(0,index)} postContent={newDesc.substring(index)} />
            }
        }
        if(audit.auditType === 'Initial') {
            return audit.changeDescription.split('\n').map((line, idx) => (
                <div key={idx}>{line}</div>
            ));
        } else if(audit.changeDescription.length > 200) {
            return <Accordion>
                <AccordionItem onClick={() => toggleDescription(audit.serviceRequestAuditID)}>
                    <AccordionTitle>
                        {audit.changeDescription.substring(0,60)}...
                        <span className='is-active'>
                            {showDescription[audit.serviceRequestAuditID] ? <i className='fa fa-minus'></i> : <i className='fa fa-plus'></i>}</span>
                    </AccordionTitle>
                    <AccordionContent
                        isActive={showDescription[audit.serviceRequestAuditID]}
                        onClick={() => toggleDescription(audit.serviceRequestAuditID)}>
                        {audit.changeDescription}
                    </AccordionContent>
                </AccordionItem>
            </Accordion>
        }
        return <>{audit.changeDescription}</>
    }

    const renderAuditRows = ()=>
    {
        if(!audits)
            return
            
        const grouped = Object.values(GroupBy<Audit>(audits, a=>a.changedOn.toString()))
        return grouped.map((auditgroup,i)=>{
            return <React.Fragment key={i}>
                <Grid className='audit-list-item'>
                    <Cell className='audit-list-item-user' small={4} large={3}>
                        <>
                            <span className='users-name'>{auditgroup[0].user.name}</span>
                            <span className='timestamp'>{format(new Date(auditgroup[0].changedOn),'M/d/yyyy p')}</span>
                        </>
                    </Cell>
                    <Cell small={8} large={9}>
                        <ul className='audit-list-item-detail'>
                            {auditgroup.map((audit,j)=> 
                                <li key={j}>
                                    {renderDescription(audit)}
                                </li>)}
                        </ul>
                    </Cell>  
                </Grid>
            </React.Fragment>
        })
    }

    return <>
        <div className='audit-list'>
            <h5><i className="fas fa-clipboard-list"></i>{title || 'Change Log'}</h5>
            {error && <div className='error-text'>{error}</div>}
            {loading && <div className='center'><img src='/spinning-circles.svg' alt='loading' style={{ paddingBottom: '1rem' }} /></div> }
            {audits && audits.length > 0 && renderAuditRows()}
            {audits && audits.length === 0 && <div className='no-changes'>No changes to display.</div>}
        </div>
    </>
}

export default ViewAudits